import { useLazyQuery } from "@apollo/client";
import { useEffect, useMemo } from "react";

import ImmersiveRecap2023 from "@/components/immersive-recap-2023";
import useAuth from "@/hooks/use-auth";

import Actions from "../actions";
import CountersButtons from "../counters-buttons";
import Layout from "../layout";
import type {
  BadgeType,
  UserDataType,
  UserType,
  UserVariablesType,
} from "../types";
import Badges from "./badges";
import Bio from "./bio";
import Bookings from "./bookings";
import { QUERY } from "./graphql";
import RightSidebar from "./right-sidebar";

export interface UserProps {
  badges: BadgeType[];
  badgesCount: number;
  user: UserType;
}

export default function User({
  badges,
  badgesCount,
  user: serverUser,
}: UserProps) {
  const { isMe, isUser, queryContext } = useAuth();
  const [fetchUser, { data }] = useLazyQuery<UserDataType, UserVariablesType>(
    QUERY
  );

  useEffect(() => {
    if (isUser) {
      fetchUser(queryContext({ isUser: true, username: serverUser.username }));
    }
  }, [serverUser, isUser, queryContext, fetchUser]);

  const user = useMemo(() => data?.user || serverUser, [data, serverUser]);

  return (
    <Layout isProfileView user={user}>
      {/* Mobile */}
      <div className="md:hidden">
        <CountersButtons user={user} />
        <Actions user={user} />
        {isMe(user) && <ImmersiveRecap2023 className="mt-[15px]" />}
        {!!user.bio && <Bio bio={user.bio} />}
        <div className="mt-5 flex flex-col items-stretch gap-7.5">
          {isMe(user) && <Bookings />}
          {!!badges.length && (
            <Badges
              badges={badges}
              count={badgesCount}
              username={user.username}
            />
          )}
        </div>
      </div>

      {/* Desktop */}
      <div className="hidden grid-cols-10 gap-20 md:grid">
        <div className="col-span-7">
          <div className="flex flex-col items-stretch gap-7.5">
            {!!user.bio && <Bio bio={user.bio} />}
            {isMe(user) && <Bookings />}
          </div>
        </div>
        <div className="col-span-3">
          <RightSidebar badges={badges} badgesCount={badgesCount} user={user} />
        </div>
      </div>
    </Layout>
  );
}
